import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaStore, FaExclamationTriangle, FaArrowRight } from 'react-icons/fa';

const NotFound = () => {
  const navigate = useNavigate();
  const [shopName, setShopName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const popularShopExamples = ['fashion-hub', 'city-mart', 'tech-world'];

  // Set document title
  useEffect(() => {
    document.title = "Find Your Shop - 4POS eCommerce";
  }, []);

  const handleShopSearch = async (e) => {
    e.preventDefault();
    if (shopName.trim()) {
      setIsLoading(true);
      
      try {
        // Check if seller exists via API
        const response = await fetch(`https://api.4posevo.online/api/search-seller?name=${encodeURIComponent(shopName.trim())}`);
        const data = await response.json();
        
        if (data.seller === null) {
          // Seller does not exist, show helpful guidance
          setErrorMessage(`We could not locate "${shopName.trim()}". Please verify the shop name or try one of the suggestions below.`);
          setIsLoading(false);
        } else {
          // Seller exists, navigate to the shop
          navigate(`/${shopName.trim().toLowerCase()}`);
        }
      } catch (error) {
        console.error('Error checking seller:', error);
        // If API fails, proceed with navigation as fallback
        navigate(`/${shopName.trim().toLowerCase()}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header Section */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-indigo-100 mb-4">
            <FaStore className="text-4xl text-indigo-600" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Let&apos;s find your shop
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
            The shop link you opened is unavailable right now. Enter the shop name below and we will take you to the correct storefront.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
            <p className="text-indigo-900 font-medium">Quick tip</p>
            <p className="text-sm text-indigo-700 mt-1">
              Use your exact shop URL name from your dashboard. Example: <span className="font-semibold">my-store</span>
            </p>
          </div>

          {/* Search Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaSearch className="mr-2 text-indigo-600" />
              Find Your Shop
            </h3>
            
            <form onSubmit={handleShopSearch} className="space-y-4">
               <div className="relative">
                 <input
                   type="text"
                   value={shopName}
                   onChange={(e) => {
                     // Replace spaces with hyphens automatically
                     const value = e.target.value.replace(/\s+/g, '-');
                     setShopName(value);
                     setErrorMessage(''); // Clear error when user types
                   }}
                   placeholder="Type your shop name (e.g. my-store)"
                   className="w-full px-4 py-4 pr-12 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 text-lg"
                   required
                 />
                 <button
                   type="submit"
                   disabled={isLoading || !shopName.trim()}
                   className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white p-2 rounded-lg transition-all duration-200"
                 >
                   {isLoading ? (
                     <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                   ) : (
                     <FaArrowRight className="w-5 h-5" />
                   )}
                 </button>
               </div>
               
               {/* Helper Text */}
               <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                 <p className="text-sky-800 text-sm flex items-center">
                   <span className="mr-2">💡</span>
                   Spaces are automatically replaced with hyphens (for example, "My Shop" becomes "My-Shop")
                 </p>
               </div>

               <div>
                 <p className="text-sm text-gray-600 mb-2">Try popular examples:</p>
                 <div className="flex flex-wrap gap-2">
                   {popularShopExamples.map((example) => (
                     <button
                       key={example}
                       type="button"
                       onClick={() => {
                         setShopName(example);
                         setErrorMessage('');
                       }}
                       className="px-3 py-1.5 rounded-full border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                     >
                       {example}
                     </button>
                   ))}
                 </div>
               </div>
               
               {/* Error Message */}
               {errorMessage && (
                 <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                   <div className="flex items-center">
                     <FaExclamationTriangle className="text-red-500 mr-2" />
                     <span className="text-red-700 text-sm">{errorMessage}</span>
                   </div>
                 </div>
               )}
               
               <button
                 type="submit"
                 disabled={isLoading || !shopName.trim()}
                 className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center"
               >
                 {isLoading ? 'Searching your shop...' : 'Continue to Shop'}
               </button>
             </form>
          </div>

          {/* Information Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FaExclamationTriangle className="mr-2 text-yellow-500" />
              How to Find Your Shop
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">For Shop Owners:</h4>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Enter your exact shop name as registered
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Spaces will be automatically replaced with hyphens
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Copy your exact shop URL from your admin panel
                  </li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">For Customers:</h4>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Ask the shop owner for the exact store URL name
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Check the shop social profile or website for the link
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-indigo-600">•</span>
                    Contact the shop directly if the link changed recently
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <div className="text-sm text-gray-500">
            Need help finding your store? Contact our support team
          </div>
        </div>

        {/* Floating WhatsApp Button Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            💡 Tip: Use the WhatsApp button in the corner for instant support
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;